const COMPANY_ID = 'VPygUw';
const LIST_ID = 'V8vdYR';
const BIS_REVISION = '2026-04-15';
const MARKETING_REVISION = '2026-07-15';
const PRODUCT_SELECT_EVENT = 'shopify:product:select';

const requestHeaders = (revision) => ({
  accept: 'application/vnd.api+json',
  'content-type': 'application/vnd.api+json',
  revision,
});

export function buildBackInStockRequest(email, variantId) {
  return {
    url: `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${COMPANY_ID}`,
    options: {
      method: 'POST',
      headers: requestHeaders(BIS_REVISION),
      body: JSON.stringify({
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            profile: { data: { type: 'profile', attributes: { email } } },
            channels: ['EMAIL'],
          },
          relationships: {
            variant: {
              data: { type: 'catalog-variant', id: `$shopify:::$default:::${variantId}` },
            },
          },
        },
      }),
    },
  };
}

export function buildMarketingRequest(email) {
  return {
    url: `https://a.klaviyo.com/client/subscriptions?company_id=${COMPANY_ID}`,
    options: {
      method: 'POST',
      headers: requestHeaders(MARKETING_REVISION),
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'Back in stock form',
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email,
                  subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
                },
              },
            },
          },
          relationships: { list: { data: { type: 'list', id: LIST_ID } } },
        },
      }),
    },
  };
}

export async function submitSubscriptions({ email, variantId, marketingConsent, fetchImpl = fetch, signal }) {
  const bisRequest = buildBackInStockRequest(email, variantId);
  const bisResponse = await fetchImpl(bisRequest.url, {
    ...bisRequest.options,
    ...(signal ? { signal } : {}),
  });
  if (!bisResponse.ok) {
    throw new Error(`Back-in-stock request failed with status ${bisResponse.status ?? 'unknown'}`);
  }

  if (!marketingConsent) {
    return { bisSucceeded: true, marketingSucceeded: null };
  }

  const marketingRequest = buildMarketingRequest(email);
  try {
    const marketingResponse = await fetchImpl(marketingRequest.url, {
      ...marketingRequest.options,
      ...(signal ? { signal } : {}),
    });
    return { bisSucceeded: true, marketingSucceeded: marketingResponse.ok };
  } catch (_error) {
    return { bisSucceeded: true, marketingSucceeded: false };
  }
}

export async function isKlaviyoAvailable(globalObject, timeoutMs = 250) {
  const modernProbe = globalObject?.klaviyo?.isIdentified;
  const legacyProbe = globalObject?._learnq?.isIdentified;
  const probe = typeof modernProbe === 'function' ? modernProbe.bind(globalObject.klaviyo) :
    typeof legacyProbe === 'function' ? legacyProbe.bind(globalObject._learnq) : null;

  if (!probe) return false;

  let timeoutId;
  try {
    await Promise.race([
      Promise.resolve(probe()),
      new Promise((_, reject) => {
        timeoutId = setTimeout(() => reject(new Error('Klaviyo readiness probe timed out')), timeoutMs);
      }),
    ]);
    return true;
  } catch (_error) {
    return false;
  } finally {
    clearTimeout(timeoutId);
  }
}

export function resolveVariantState({ markerEligible, resource }) {
  return {
    eligible: markerEligible === true && resource?.available === false,
    variantId: resource?.id == null ? '' : String(resource.id),
  };
}

const HTMLElementBase = globalThis.HTMLElement ?? class {};

class SojanBackInStock extends HTMLElementBase {
  connectedCallback() {
    this.disconnected = false;
    const connectionGeneration = (this.connectionGeneration ?? 0) + 1;
    this.connectionGeneration = connectionGeneration;
    this.ready = false;
    this.isSubmitting = false;
    this.eligible = this.dataset.bisEligible === 'true';
    this.variantId = this.dataset.variantId || '';
    this.section = this.closest('.shopify-section, dialog, product-card');

    this.bindControls();
    this.section?.addEventListener(PRODUCT_SELECT_EVENT, this.handleProductSelect);

    this.syncVisibility();
    this.readinessPromise = this.waitForKlaviyo(connectionGeneration);
  }

  disconnectedCallback() {
    this.disconnected = true;
    this.connectionGeneration = (this.connectionGeneration ?? 0) + 1;
    this.ready = false;
    this.hidden = true;
    this.variantGeneration = (this.variantGeneration ?? 0) + 1;
    this.cancelSubmission();
    if (this.dialog?.open && typeof this.dialog.close === 'function') this.dialog.close();
    this.unbindControls();
    this.section?.removeEventListener(PRODUCT_SELECT_EVENT, this.handleProductSelect);
  }

  bindControls() {
    this.unbindControls();
    this.dialog = this.querySelector('.sojan-bis__dialog');
    this.trigger = this.querySelector('.sojan-bis__trigger');
    this.closeButton = this.querySelector('.sojan-bis__close');
    this.submitButton = this.querySelector('.sojan-bis__submit');
    this.emailInput = this.querySelector('.sojan-bis__email');
    this.consentInput = this.querySelector('.sojan-bis__consent-input');
    this.status = this.querySelector('.sojan-bis__status');
    this.trigger?.addEventListener('click', this.openDialog);
    this.closeButton?.addEventListener('click', this.closeDialog);
    this.submitButton?.addEventListener('click', this.handleSubmit);
    this.emailInput?.addEventListener('keydown', this.handleEmailKeydown);
  }

  unbindControls() {
    this.trigger?.removeEventListener('click', this.openDialog);
    this.closeButton?.removeEventListener('click', this.closeDialog);
    this.submitButton?.removeEventListener('click', this.handleSubmit);
    this.emailInput?.removeEventListener('keydown', this.handleEmailKeydown);
  }

  waitForKlaviyo = async (connectionGeneration) => {
    const attempts = this.readinessAttempts ?? 12;
    const delay = this.readinessDelay ?? 100;
    const probe = this.readinessProbe ?? isKlaviyoAvailable;
    const runtimeGlobal = this.runtimeGlobal ?? window;

    for (let attempt = 0; attempt < attempts; attempt += 1) {
      if (this.disconnected || connectionGeneration !== this.connectionGeneration) return;
      const runtimeReady = await probe(runtimeGlobal);
      if (this.disconnected || connectionGeneration !== this.connectionGeneration) return;
      if (runtimeReady) {
        this.ready = true;
        this.syncVisibility();
        return;
      }
      if (attempt + 1 < attempts) {
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
    if (this.disconnected || connectionGeneration !== this.connectionGeneration) return;
    this.ready = false;
    this.syncVisibility();
  };

  syncVisibility() {
    const dialogReady = typeof this.dialog?.showModal === 'function' && typeof this.dialog?.close === 'function';
    this.hidden = !(this.ready && dialogReady && this.eligible && this.variantId);
    if (this.hidden && this.dialog?.open && typeof this.dialog.close === 'function') this.dialog.close();
  }

  openDialog = () => {
    if (this.hidden || typeof this.dialog?.showModal !== 'function') return;
    this.status.textContent = '';
    this.status.removeAttribute('data-state');
    this.dialog.showModal();
    this.emailInput?.focus();
  };

  closeDialog = () => {
    if (typeof this.dialog?.close === 'function') this.dialog.close();
    this.trigger?.focus();
  };

  handleEmailKeydown = (event) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    this.handleSubmit();
  };

  handleSubmit = async () => {
    if (this.isSubmitting) return;
    if (!this.emailInput?.checkValidity()) {
      this.emailInput?.reportValidity();
      return;
    }

    this.isSubmitting = true;
    const submissionGeneration = (this.submissionGeneration ?? 0) + 1;
    this.submissionGeneration = submissionGeneration;
    const controller = new AbortController();
    this.submissionController = controller;
    const timeoutMs = this.submissionTimeoutMs ?? 10000;
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    this.submissionTimeoutId = timeoutId;
    this.submitButton.disabled = true;
    this.status.textContent = 'Saving your request...';
    this.status.dataset.state = 'pending';

    try {
      const result = await submitSubscriptions({
        email: this.emailInput.value.trim(),
        variantId: this.variantId,
        marketingConsent: this.consentInput.checked,
        fetchImpl: this.fetchImpl,
        signal: controller.signal,
      });
      if (this.disconnected || submissionGeneration !== this.submissionGeneration) return;
      this.bisSucceeded = result.bisSucceeded;
      this.status.textContent = 'You’ve joined the waitlist. We’ll email you when it’s back.';
      this.status.dataset.state = 'success';
      if (result.marketingSucceeded === false) {
        this.status.textContent += ' We’ll notify you. We couldn’t sign you up to product drops, please try again.';
        this.status.dataset.state = 'warning';
      }
    } catch (_error) {
      if (this.disconnected || submissionGeneration !== this.submissionGeneration) return;
      this.status.textContent = 'That didn’t save. Please try again.';
      this.status.dataset.state = 'error';
    } finally {
      clearTimeout(timeoutId);
      if (submissionGeneration === this.submissionGeneration) {
        this.submissionController = null;
        this.submissionTimeoutId = null;
        this.isSubmitting = false;
        this.submitButton.disabled = false;
      }
    }
  };

  cancelSubmission() {
    this.submissionGeneration = (this.submissionGeneration ?? 0) + 1;
    this.submissionController?.abort();
    clearTimeout(this.submissionTimeoutId);
    this.submissionController = null;
    this.submissionTimeoutId = null;
    this.isSubmitting = false;
    if (this.submitButton) this.submitButton.disabled = false;
    if (this.status?.dataset.state === 'pending') {
      this.status.textContent = '';
      this.status.removeAttribute('data-state');
    }
  }

  handleProductSelect = async (event) => {
    this.cancelSubmission();
    this.eligible = false;
    this.syncVisibility();
    const generation = (this.variantGeneration ?? 0) + 1;
    this.variantGeneration = generation;

    try {
      const { detail } = await event.promise;
      if (this.disconnected || generation !== this.variantGeneration || !detail?.html) return;

      this.bindControls();

      const marker = Array.from(detail.html.querySelectorAll('sojan-back-in-stock')).find(
        (candidate) => candidate.dataset.sectionId === this.dataset.sectionId
      );
      const state = resolveVariantState({
        markerEligible: marker?.dataset.bisEligible === 'true',
        resource: detail.resource,
      });

      this.eligible = state.eligible;
      this.variantId = state.variantId;
      this.dataset.bisEligible = String(state.eligible);
      this.dataset.variantId = state.variantId;
      this.syncVisibility();
    } catch (error) {
      if (error?.name !== 'AbortError') console.warn('[back-in-stock] Variant update failed', error);
    }
  };
}

if (globalThis.customElements && !customElements.get('sojan-back-in-stock')) {
  customElements.define('sojan-back-in-stock', SojanBackInStock);
}
